import { useEffect, useState } from 'react';

import { fmt } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { PREPAID_ASSET_CODE, fmtPeriod } from './pp-helpers';
import { useAmortizePreview, type useAmortizePrepaid } from '../../lib/queries';

export interface RunModalProps {
  companyId: string | undefined;
  initialPeriod: string; // YYYYMM — driven by the page's period selector
  onClose: () => void;
  submitting: boolean;
  amortizeMut: ReturnType<typeof useAmortizePrepaid>;
}

export function PPRunModal({
  companyId,
  initialPeriod,
  onClose,
  submitting,
  amortizeMut,
}: RunModalProps) {
  const [fromPeriod, setFromPeriod] = useState<string>(initialPeriod);
  const [toPeriod, setToPeriod] = useState<string>(initialPeriod);
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());
  const previewMut = useAmortizePreview(companyId);
  const previewData = previewMut.data;

  // Auto-fetch preview on open and when from/to changes
  const doPreview = (): void => {
    if (!companyId) return;
    previewMut.mutate({ fromPeriod, toPeriod: toPeriod !== fromPeriod ? toPeriod : undefined });
  };

  // Fire preview on mount (initial load)
  useEffect(() => {
    doPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExclude = (contractId: string): void => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(contractId)) {
        next.delete(contractId);
      } else {
        next.add(contractId);
      }
      return next;
    });
  };

  // Compute included grand total from preview data (client-side, excludes unchecked rows)
  const includedTotal = previewData
    ? previewData.periods.reduce(
        (pSum, p) =>
          pSum +
          p.lines
            .filter((l) => !excludedIds.has(l.contractId))
            .reduce((lSum, l) => lSum + Number(l.amount), 0),
        0,
      )
    : 0;

  const includedCount = previewData
    ? previewData.periods.reduce(
        (s, p) => s + p.lines.filter((l) => !excludedIds.has(l.contractId)).length,
        0,
      )
    : 0;

  const periodLabel =
    fromPeriod === toPeriod
      ? fmtPeriod(fromPeriod)
      : `${fmtPeriod(fromPeriod)} – ${fmtPeriod(toPeriod)}`;

  const onConfirm = (): void => {
    if (!companyId) return;
    const excluded = [...excludedIds];
    amortizeMut.mutate(
      {
        data:
          fromPeriod === toPeriod
            ? { fromPeriod, excludeContractIds: excluded.length ? excluded : undefined }
            : { fromPeriod, toPeriod, excludeContractIds: excluded.length ? excluded : undefined },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 980,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              ประมวลผลตัดจ่ายค่าใช้จ่ายล่วงหน้า — {periodLabel}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              {previewData ? (
                <>
                  {previewData.contractsTotal} สัญญา ·{' '}
                  {previewData.periods.length > 1
                    ? `${previewData.periods.length} งวด`
                    : `งวด ${fmtPeriod(fromPeriod)}`}{' '}
                  · ยอดรวม (ที่เลือก) <b style={{ color: 'var(--brand)' }}>{fmt(includedTotal)}</b>{' '}
                  บาท
                </>
              ) : previewMut.isPending ? (
                'กำลังโหลดข้อมูล…'
              ) : (
                'คลิก "ดู JV ก่อนรัน" เพื่อโหลดข้อมูล'
              )}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div style={{ padding: '18px 22px', overflow: 'auto', flex: 1 }}>
          {/* Catch-up range selector */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4,1fr)',
              gap: 14,
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid var(--border)',
              marginBottom: 16,
            }}
          >
            <div>
              <div className="meta">งวดเริ่มต้น</div>
              <select
                className="select"
                value={fromPeriod}
                onChange={(e) => setFromPeriod(e.target.value)}
                style={{ marginTop: 4, height: 34, fontSize: 14, width: '100%' }}
              >
                <option value="202603">มี.ค. 2026</option>
                <option value="202604">เม.ย. 2026</option>
                <option value="202605">พ.ค. 2026</option>
                <option value="202606">มิ.ย. 2026</option>
              </select>
            </div>
            <div>
              <div className="meta">งวดสิ้นสุด</div>
              <select
                className="select"
                value={toPeriod}
                onChange={(e) => setToPeriod(e.target.value)}
                style={{ marginTop: 4, height: 34, fontSize: 14, width: '100%' }}
              >
                <option value="202603">มี.ค. 2026</option>
                <option value="202604">เม.ย. 2026</option>
                <option value="202605">พ.ค. 2026</option>
                <option value="202606">มิ.ย. 2026</option>
              </select>
            </div>
            <div>
              <div className="meta">จำนวนรายการ (ที่เลือก)</div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginTop: 4,
                  fontFamily: 'var(--font-display)',
                }}
              >
                {previewData ? `${includedCount} รายการ` : '—'}
              </div>
            </div>
            <div>
              <div className="meta">ยอดตัดจ่ายรวม (ที่เลือก)</div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginTop: 4,
                  color: 'var(--brand)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {previewData ? fmt(includedTotal) : '—'}
              </div>
            </div>
          </div>

          {previewMut.isPending && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
              กำลังโหลดข้อมูลตัวอย่าง…
            </div>
          )}

          {previewMut.isError && (
            <div
              style={{
                padding: '12px 14px',
                background: '#fef2f2',
                borderRadius: 8,
                border: '1px solid var(--negative)',
                marginBottom: 12,
                color: 'var(--negative)',
                fontSize: 14,
              }}
            >
              เกิดข้อผิดพลาดในการโหลด preview — กรุณาลองใหม่
            </div>
          )}

          {previewData && (
            <>
              {previewData.periods.map((p) => (
                <div key={p.period} style={{ marginBottom: 16 }}>
                  {/* Period header + warnings */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      marginBottom: 6,
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    <span>งวด {fmtPeriod(p.period)}</span>
                    {!p.periodOpen && (
                      <span
                        className="badge"
                        style={{
                          color: 'var(--negative)',
                          background: '#fef2f2',
                          border: '1px solid var(--negative)',
                        }}
                      >
                        งวดปิดแล้ว
                      </span>
                    )}
                    {p.warnings.map((w, wi) => (
                      <span
                        key={wi}
                        className="badge warning"
                        style={{ fontWeight: 400, fontSize: 13 }}
                      >
                        {w}
                      </span>
                    ))}
                  </div>
                  <div
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      overflow: 'hidden',
                    }}
                  >
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th style={{ width: 24 }}>
                            <input type="checkbox" defaultChecked disabled />
                          </th>
                          <th>รหัส</th>
                          <th>รายละเอียด</th>
                          <th>บัญชีค่าใช้จ่าย</th>
                          <th>เครดิต</th>
                          <th className="num">จำนวน</th>
                          <th className="num">คงเหลือหลังตัด</th>
                        </tr>
                      </thead>
                      <tbody>
                        {p.lines.map((l) => {
                          const included = !excludedIds.has(l.contractId);
                          return (
                            <tr
                              key={l.contractId}
                              style={!included ? { opacity: 0.45 } : undefined}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  checked={included}
                                  onChange={() => toggleExclude(l.contractId)}
                                />
                              </td>
                              <td className="code">{l.contractCode}</td>
                              <td>
                                <div style={{ fontWeight: 500 }}>{l.description.slice(0, 60)}</div>
                                {l.isFinal && (
                                  <div className="meta" style={{ color: 'var(--positive)' }}>
                                    งวดสุดท้าย
                                  </div>
                                )}
                              </td>
                              <td className="code muted">{l.expenseAccountCode}</td>
                              <td className="code muted">{PREPAID_ASSET_CODE}</td>
                              <td className="num tnum">{fmt(Number(l.amount))}</td>
                              <td className="num tnum">{fmt(Number(l.remainingBalance))}</td>
                            </tr>
                          );
                        })}
                        {p.skipped > 0 && (
                          <tr>
                            <td
                              colSpan={7}
                              className="faint"
                              style={{ textAlign: 'center', padding: '6px 8px', fontSize: 13 }}
                            >
                              ข้าม {p.skipped} รายการ (ตัดจ่ายครบแล้ว / ยังไม่เริ่ม)
                            </td>
                          </tr>
                        )}
                        <tr className="total">
                          <td colSpan={5}>
                            รวมงวด {fmtPeriod(p.period)} —{' '}
                            {p.lines.filter((l) => !excludedIds.has(l.contractId)).length} รายการ
                            ที่เลือก
                          </td>
                          <td className="num tnum" style={{ color: 'var(--brand)' }}>
                            {fmt(
                              p.lines
                                .filter((l) => !excludedIds.has(l.contractId))
                                .reduce((s, l) => s + Number(l.amount), 0),
                            )}
                          </td>
                          <td />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              <div
                style={{
                  marginTop: 14,
                  padding: '12px 14px',
                  background: '#e8eff8',
                  borderRadius: 8,
                  border: '1px solid #c1d1e8',
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: 'var(--info)',
                    color: 'white',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 14,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  i
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.55 }}>
                  ระบบจะสร้างใบสำคัญรวม{' '}
                  {previewData.periods.length > 1 ? `(${previewData.periods.length} ใบ)` : ''} รวม{' '}
                  {includedCount} รายการ · ยอด{' '}
                  <b style={{ color: 'var(--brand)' }}>{fmt(includedTotal)}</b> บาท ·
                  ใบสำคัญจะลงสถานะ <b>Posted</b> ทันทีและไม่สามารถลบได้ (แก้ไขโดยใช้ Reversing JV
                  เท่านั้น)
                </div>
              </div>
            </>
          )}
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={doPreview} disabled={previewMut.isPending}>
              {previewMut.isPending ? 'กำลังโหลด…' : 'ดู JV ก่อนรัน'}
            </button>
            <button
              className="btn primary"
              onClick={onConfirm}
              disabled={submitting || !previewData || includedCount === 0}
            >
              <Icon name="check" size={14} /> {submitting ? 'กำลังประมวลผล…' : 'ประมวลผล & ลง JV'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
